import { Bar, BarChart, CartesianGrid, Line, LineChart, ReferenceArea, XAxis, YAxis } from 'recharts';
import { useAnimatedDomain } from '../../hooks/useAnimatedDomain';
import { cn } from '../../utils/cn';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../ui/chart';

type AxisDomain = [number | string, number | string];

/**
 * True when the domain is a concrete numeric `[min, max]` pair (not `['dataMin', 'dataMax']` or auto),
 * i.e. a bounded window the axis should clip to rather than widen past. Drives `allowDataOverflow`.
 */
const hasBoundedNumericDomain = (domain: AxisDomain | undefined): boolean =>
  Array.isArray(domain) && typeof domain[0] === 'number' && typeof domain[1] === 'number';

export interface SeriesChartProps {
  /** Line (default) or bar series. */
  type?: 'line' | 'bar';
  /** Normalized points. `x` is an epoch-ms number (time axis) or a category label string. */
  data: { x: number | string; y: number | null; y2?: number | null }[];
  /** Series label + color, keyed `y` / `y2`. Drives the legend, tooltip, and line/bar colors. */
  config: ChartConfig;
  xAxis?: {
    type?: 'number' | 'category';
    scale?: 'time' | 'auto';
    domain?: AxisDomain;
    tickFormatter?: (value: number) => string;
    label?: string;
  };
  yAxis?: { domain?: AxisDomain; label?: string; tickFormatter?: (value: number) => string };
  tooltipLabelFormatter?: (value: unknown) => string;
  /** Format each series value in the tooltip (e.g. minutes → "5h 30m"); defaults to a grouped number. */
  valueFormatter?: (value: number) => string;
  referenceRange?: { low: number | null; high: number | null; label?: string };
  /** Reduce the second series' opacity (e.g. BP diastolic shares the systolic color at 0.5). */
  secondSeriesOpacity?: number;
  showLegend?: boolean;
  /** Drop the connecting line and render points as solid dots only (used by the care-plan charts). */
  dotsOnly?: boolean;
  /** Hide the x-axis (the care-plan detail relies on the structural timeline + tooltips for dates). */
  hideXAxis?: boolean;
  /**
   * Shared identifier that ties several charts into one synchronized group: hovering one moves the
   * tooltip + cursor on all charts sharing this id. Pair with `syncMethod="value"` so points match by
   * x-value across differently-sampled series (recharts' default `'index'` would misalign them).
   */
  syncId?: string;
  syncMethod?: 'index' | 'value';
  className?: string;
}

const xAxisLabelProps = (
  value: string,
): { value: string; position: 'insideBottom'; offset: number; style: { fontSize: number } } => ({
  value,
  position: 'insideBottom' as const,
  offset: -5,
  style: { fontSize: 12 },
});

const yAxisLabelProps = (
  value: string,
): { value: string; angle: number; position: 'insideLeft'; style: { textAnchor: 'middle'; fontSize: number } } => ({
  value,
  angle: -90,
  position: 'insideLeft' as const,
  style: { textAnchor: 'middle' as const, fontSize: 12 },
});

type DotConfig = { r: number; fill?: string; stroke?: string; opacity?: number };
/** Dots-only renders solid dots in the series color (recharts defaults to a white fill); otherwise the default open dot. */
const dotConfig = (dotsOnly: boolean | undefined, colorVar: string, opacity?: number): DotConfig =>
  dotsOnly ? { r: 3, fill: colorVar, stroke: colorVar, opacity } : { r: 4, opacity };
const seriesStroke = (dotsOnly: boolean | undefined, colorVar: string): string => (dotsOnly ? 'none' : colorVar);

const hasRange = (
  range?: SeriesChartProps['referenceRange'],
): range is { low: number | null; high: number | null; label?: string } =>
  !!range && (range.low !== null || range.high !== null);

/** Build a tooltip row renderer that formats the value (e.g. minutes → "5h 30m") while keeping the
 * default dot + label + value layout; returns undefined when no custom formatter is supplied. */
const buildTooltipFormatter = (
  config: ChartConfig,
  valueFormatter: ((value: number) => string) | undefined,
):
  | ((
      value: number | string | readonly (number | string)[] | undefined,
      name: number | string | undefined,
    ) => React.ReactNode)
  | undefined => {
  if (!valueFormatter) return undefined;
  return (value, name) => {
    const key = String(name);
    return (
      <>
        <div className="size-2.5 shrink-0 rounded-[2px]" style={{ backgroundColor: `var(--color-${key})` }} />
        <div className="flex flex-1 items-center justify-between leading-none">
          <span className="text-muted-foreground">{config[key]?.label ?? name}</span>
          <span className="font-mono font-medium text-foreground tabular-nums">{valueFormatter(Number(value))}</span>
        </div>
      </>
    );
  };
};

/**
 * Shared presentational trend chart used by the patientpedia (chat), records (labs), and care-plan
 * detail charts. Owns the recharts scaffold — container, grid, labeled axes, tooltip, optional
 * reference range, one or two series, optional legend — so call sites only adapt their own data and
 * supply config. The compact `TrendChart` sparkline is intentionally separate.
 */
export function SeriesChart({
  type = 'line',
  data,
  config,
  xAxis,
  yAxis,
  tooltipLabelFormatter,
  valueFormatter,
  referenceRange,
  secondSeriesOpacity,
  showLegend,
  dotsOnly,
  hideXAxis,
  syncId,
  syncMethod,
  className,
}: SeriesChartProps): React.ReactNode {
  const hasSecondSeries = 'y2' in config;

  // Glide between zoom windows instead of snapping when a bounded numeric domain (the timeline's zoom
  // window) changes; non-numeric domains (e.g. `['dataMin', 'dataMax']`) pass through unanimated.
  const animatedXDomain = useAnimatedDomain(xAxis?.domain);

  // A custom value formatter (e.g. minutes → "5h 30m") replaces the tooltip row's default number.
  const tooltipFormatter = buildTooltipFormatter(config, valueFormatter);
  const { domain: yDomain, label: yLabel, tickFormatter: yTickFormatter } = yAxis ?? {};

  const sharedAxes = (
    <>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis
        dataKey="x"
        type={xAxis?.type}
        scale={xAxis?.scale}
        domain={animatedXDomain}
        // When a bounded numeric domain is supplied (e.g. the timeline's zoom window), clip to it rather
        // than letting recharts widen the axis back out to the full data extent.
        allowDataOverflow={hasBoundedNumericDomain(xAxis?.domain)}
        hide={hideXAxis}
        tickFormatter={xAxis?.tickFormatter}
        tickLine={false}
        axisLine={false}
        tickMargin={8}
        fontSize={12}
        label={xAxis?.label ? xAxisLabelProps(xAxis.label) : undefined}
      />
      <YAxis
        domain={yDomain}
        tickLine={false}
        axisLine={false}
        tickMargin={8}
        fontSize={12}
        tickFormatter={yTickFormatter}
        label={yLabel ? yAxisLabelProps(yLabel) : undefined}
      />
      {/* Snap to the cursor — without this, synced sibling charts (shared syncId) slide their tooltip in. */}
      <ChartTooltip
        content={<ChartTooltipContent formatter={tooltipFormatter} />}
        labelFormatter={tooltipLabelFormatter}
        isAnimationActive={false}
      />
      {hasRange(referenceRange) && (
        <ReferenceArea
          y1={referenceRange.low ?? undefined}
          y2={referenceRange.high ?? undefined}
          fill="var(--color-primary)"
          fillOpacity={0.08}
          strokeOpacity={0}
          label={
            referenceRange.label
              ? {
                  value: referenceRange.label,
                  position: 'insideTopRight' as const,
                  fontSize: 10,
                  fill: 'var(--color-muted-foreground)',
                }
              : undefined
          }
        />
      )}
      {showLegend && <ChartLegend content={<ChartLegendContent />} />}
    </>
  );

  return (
    <ChartContainer config={config} className={cn('h-[250px] w-full', className)}>
      {type === 'line' ? (
        <LineChart
          data={data}
          margin={{ top: 8, right: 12, bottom: 8, left: 8 }}
          syncId={syncId}
          syncMethod={syncMethod}
          accessibilityLayer
        >
          {sharedAxes}
          <Line
            type="monotone"
            dataKey="y"
            stroke={seriesStroke(dotsOnly, 'var(--color-y)')}
            strokeWidth={2}
            dot={dotConfig(dotsOnly, 'var(--color-y)')}
            activeDot={{ r: 6 }}
            isAnimationActive={!dotsOnly}
          />
          {hasSecondSeries && (
            <Line
              type="monotone"
              dataKey="y2"
              stroke={seriesStroke(dotsOnly, 'var(--color-y2)')}
              strokeWidth={2}
              strokeOpacity={secondSeriesOpacity}
              dot={dotConfig(dotsOnly, 'var(--color-y2)', secondSeriesOpacity)}
              activeDot={{ r: 6, opacity: secondSeriesOpacity }}
              isAnimationActive={!dotsOnly}
            />
          )}
        </LineChart>
      ) : (
        <BarChart
          data={data}
          margin={{ top: 8, right: 12, bottom: 8, left: 8 }}
          syncId={syncId}
          syncMethod={syncMethod}
          accessibilityLayer
        >
          {sharedAxes}
          <Bar dataKey="y" fill="var(--color-y)" radius={[4, 4, 0, 0]} />
          {hasSecondSeries && (
            <Bar dataKey="y2" fill="var(--color-y2)" fillOpacity={secondSeriesOpacity} radius={[4, 4, 0, 0]} />
          )}
        </BarChart>
      )}
    </ChartContainer>
  );
}
